/**
 * Filtros do Mapa: um botão discreto ("Filtros" + quantos estão em uso) que
 * abre um painel compacto com TODOS os filtros de filtros.js, agrupados
 * (Onde, Quem, Ambiente, Situação, Prazo), e os chips removíveis do que
 * está em uso. A busca livre fica fora do painel, sempre à mão na barra.
 *
 * O painel é montado a partir de DEFINICOES_FILTROS: filtro novo lá
 * aparece aqui sem mexer nesta tela.
 */
import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { FiChevronDown, FiSliders, FiX } from "react-icons/fi";

import { normalizeText } from "../../utils/data";
import { plural } from "../../utils/dinheiro";
import { DEFINICOES_FILTROS, GRUPOS_FILTROS, opcoesDoFiltro, resumoDosFiltros } from "./dados/filtros";
import {
  BotaoFiltro,
  CampoFiltro,
  Chip,
  Chips,
  GradeFiltros,
  GrupoFiltro,
  ListaOpcoes,
  OpcaoAlternavel,
  PainelFiltro,
  RodapeFiltro,
  Segmentado,
  SeletorFiltro,
  TopoFiltro,
  EnvoltorioFiltro,
} from "./styles";

// Poucas opções fixas viram botões alternáveis; o resto vira lista com busca.
const LIMITE_ALTERNAVEIS = 6;
const LIMITE_RESUMO = 2;

function ehAlternavel(definicao, opcoes) {
  return Boolean(definicao.opcoes || definicao.id === "setores") && opcoes.length <= LIMITE_ALTERNAVEIS;
}

function alternar(lista, valor) {
  return lista.includes(valor) ? lista.filter((v) => v !== valor) : [...lista, valor];
}

function OpcoesAlternaveis({ definicao, valor, opcoes, onMudar }) {
  return (
    <div className="alternaveis" role="group" aria-label={definicao.rotulo}>
      {opcoes.map((opcao) => {
        const ligado = valor.includes(opcao.valor);
        return (
          <OpcaoAlternavel
            key={opcao.valor}
            type="button"
            aria-pressed={ligado}
            onClick={() => onMudar(alternar(valor, opcao.valor))}
          >
            {opcao.rotulo}
          </OpcaoAlternavel>
        );
      })}
    </div>
  );
}

function resumoDaSelecao(valor, opcoes) {
  if (!valor.length) return "Todos";
  const rotulos = valor.map((v) => opcoes.find((o) => o.valor === v)?.rotulo || v);
  const primeiros = rotulos.slice(0, LIMITE_RESUMO).join(", ");
  return rotulos.length > LIMITE_RESUMO ? `${primeiros} +${rotulos.length - LIMITE_RESUMO}` : primeiros;
}

function SeletorMultiplo({ definicao, valor, opcoes, aberto, onAlternarAberto, onMudar }) {
  const [busca, setBusca] = useState("");
  const idLista = useId();
  const visiveis = useMemo(() => {
    const alvo = normalizeText(busca);
    return alvo ? opcoes.filter((o) => normalizeText(o.rotulo).includes(alvo)) : opcoes;
  }, [busca, opcoes]);
  return (
    <div className="seletor">
      <SeletorFiltro
        type="button"
        aria-expanded={aberto}
        aria-controls={idLista}
        data-ativo={valor.length ? "sim" : undefined}
        onClick={onAlternarAberto}
      >
        <span className="valor">{resumoDaSelecao(valor, opcoes)}</span>
        <FiChevronDown aria-hidden="true" />
      </SeletorFiltro>
      {aberto && (
        <ListaOpcoes id={idLista}>
          {opcoes.length > LIMITE_ALTERNAVEIS && (
            <input
              type="search"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder={`Buscar ${definicao.rotulo.toLocaleLowerCase("pt-BR")}`}
              aria-label={`Buscar ${definicao.rotulo.toLocaleLowerCase("pt-BR")}`}
            />
          )}
          <ul>
            {visiveis.map((opcao) => (
              <li key={opcao.valor}>
                <label>
                  <input
                    type="checkbox"
                    checked={valor.includes(opcao.valor)}
                    onChange={() => onMudar(alternar(valor, opcao.valor))}
                  />
                  <span>{opcao.rotulo}</span>
                  {opcao.inativo && <span className="inativo">inativo</span>}
                </label>
              </li>
            ))}
            {!visiveis.length && <li className="vazio">Nada encontrado.</li>}
          </ul>
          {valor.length > 0 && (
            <button type="button" className="limpar" onClick={() => onMudar([])}>Limpar seleção</button>
          )}
        </ListaOpcoes>
      )}
    </div>
  );
}

function OpcaoUnica({ definicao, valor, onMudar }) {
  const opcoes = [{ valor: "", rotulo: "Todos" }, ...definicao.opcoes];
  return (
    <Segmentado role="group" aria-label={definicao.rotulo}>
      {opcoes.map((opcao) => (
        <button key={opcao.valor || "todos"} type="button" aria-pressed={valor === opcao.valor} onClick={() => onMudar(opcao.valor)}>
          {opcao.rotulo}
        </button>
      ))}
    </Segmentado>
  );
}

function Periodo({ definicao, valor, onMudar }) {
  return (
    <div className="periodo" role="group" aria-label={definicao.rotulo}>
      <label>
        <span>De</span>
        <input type="date" value={valor.de} max={valor.ate || undefined} onChange={(e) => onMudar({ ...valor, de: e.target.value })} />
      </label>
      <label>
        <span>Até</span>
        <input type="date" value={valor.ate} min={valor.de || undefined} onChange={(e) => onMudar({ ...valor, ate: e.target.value })} />
      </label>
    </div>
  );
}

function Campo({ definicao, filtros, contexto, abertoId, setAbertoId, definirFiltro }) {
  const valor = filtros[definicao.id];
  const onMudar = (novo) => definirFiltro(definicao.id, novo);
  let controle;
  // Campo de fichinhas com muitas opções ocupa duas colunas: numa coluna só,
  // "Terceiros pendentes" e "Acabamento pendente" caíam uma por linha e o
  // resto da faixa ficava vazio.
  let largo = false;
  if (definicao.tipo === "opcao") {
    controle = <OpcaoUnica definicao={definicao} valor={valor} onMudar={onMudar} />;
  } else if (definicao.tipo === "periodo") {
    controle = <Periodo definicao={definicao} valor={valor} onMudar={onMudar} />;
  } else {
    const opcoes = opcoesDoFiltro(definicao.id, contexto);
    largo = ehAlternavel(definicao, opcoes) && opcoes.length > 2;
    controle = ehAlternavel(definicao, opcoes) ? (
      <OpcoesAlternaveis definicao={definicao} valor={valor} opcoes={opcoes} onMudar={onMudar} />
    ) : (
      <SeletorMultiplo
        definicao={definicao}
        valor={valor}
        opcoes={opcoes}
        aberto={abertoId === definicao.id}
        onAlternarAberto={() => setAbertoId((atual) => (atual === definicao.id ? null : definicao.id))}
        onMudar={onMudar}
      />
    );
  }
  return (
    <CampoFiltro data-largo={largo ? "sim" : undefined}>
      <span className="rotulo">{definicao.rotulo}</span>
      {controle}
    </CampoFiltro>
  );
}

function PainelDeFiltros({ id, filtros, definirFiltro, limparFiltros, totalAtivos, contexto, totais, onFechar }) {
  const [abertoId, setAbertoId] = useState(null);
  return (
    <PainelFiltro id={id} role="dialog" aria-label="Filtros do mapa">
      <TopoFiltro>
        <strong>Filtros</strong>
        <button type="button" className="limpar" disabled={!totalAtivos} onClick={limparFiltros}>Limpar tudo</button>
        <button type="button" className="fechar" aria-label="Fechar filtros" onClick={onFechar}>
          <FiX aria-hidden="true" />
        </button>
      </TopoFiltro>
      {GRUPOS_FILTROS.map((grupo) => {
        const definicoes = DEFINICOES_FILTROS.filter((d) => d.grupo === grupo.id);
        return (
          <GrupoFiltro key={grupo.id} aria-label={grupo.rotulo}>
            <h3>{grupo.rotulo}</h3>
            <GradeFiltros>
              {definicoes.map((definicao) => (
                <Campo
                  key={definicao.id}
                  definicao={definicao}
                  filtros={filtros}
                  contexto={contexto}
                  abertoId={abertoId}
                  setAbertoId={setAbertoId}
                  definirFiltro={definirFiltro}
                />
              ))}
            </GradeFiltros>
          </GrupoFiltro>
        );
      })}
      <RodapeFiltro role="status">
        {`${plural(totais.ambientes, "ambiente", "ambientes")} e ${plural(totais.tarefas, "tarefa", "tarefas")} no filtro`}
      </RodapeFiltro>
    </PainelFiltro>
  );
}

/**
 * Botão + painel. `contexto` = {demandas, estado} (opções dinâmicas:
 * clientes, pedidos, lotes, postos…); `totais` = {ambientes, tarefas}.
 */
export default function FiltrosDoMapa({ filtros, definirFiltro, limparFiltros, totalAtivos, contexto, totais }) {
  const [aberto, setAberto] = useState(false);
  const envoltorioRef = useRef(null);
  const botaoRef = useRef(null);
  const idPainel = useId();
  const semBusca = totalAtivos - (String(filtros.busca || "").trim() ? 1 : 0);

  useEffect(() => {
    if (!aberto) return undefined;
    const aoTocarFora = (e) => {
      if (!envoltorioRef.current?.contains(e.target)) setAberto(false);
    };
    const aoTeclar = (e) => {
      if (e.key !== "Escape") return;
      setAberto(false);
      botaoRef.current?.focus();
    };
    document.addEventListener("pointerdown", aoTocarFora, true);
    document.addEventListener("keydown", aoTeclar);
    return () => {
      document.removeEventListener("pointerdown", aoTocarFora, true);
      document.removeEventListener("keydown", aoTeclar);
    };
  }, [aberto]);

  return (
    <EnvoltorioFiltro ref={envoltorioRef}>
      <BotaoFiltro
        ref={botaoRef}
        type="button"
        aria-expanded={aberto}
        aria-controls={idPainel}
        data-ativo={semBusca > 0 ? "sim" : undefined}
        onClick={() => setAberto((v) => !v)}
      >
        <FiSliders aria-hidden="true" />
        <span>Filtros</span>
        {semBusca > 0 && <span className="contador" aria-label={`${semBusca} em uso`}>{semBusca}</span>}
      </BotaoFiltro>
      {aberto && (
        <PainelDeFiltros
          id={idPainel}
          filtros={filtros}
          definirFiltro={definirFiltro}
          limparFiltros={limparFiltros}
          totalAtivos={semBusca}
          contexto={contexto}
          totais={totais}
          onFechar={() => setAberto(false)}
        />
      )}
    </EnvoltorioFiltro>
  );
}

/** Chips do que está em uso (menos a busca, que já aparece no campo). */
export function ChipsDosFiltros({ filtros, contexto, onRemover, onLimpar }) {
  const chips = resumoDosFiltros(filtros, contexto).filter((c) => c.id !== "busca");
  if (!chips.length) return null;
  return (
    <Chips role="group" aria-label="Filtros em uso">
      {chips.map((chip) => (
        <Chip key={chip.id}>
          <span className="rotulo">{chip.rotulo}:</span>
          <span className="valor" title={chip.valor}>{chip.valor}</span>
          <button type="button" aria-label={`Tirar o filtro ${chip.rotulo}`} onClick={() => onRemover(chip.id)}>
            <FiX aria-hidden="true" />
          </button>
        </Chip>
      ))}
      {chips.length > 1 && (
        <button type="button" className="limpar-todos" onClick={onLimpar}>Limpar filtros</button>
      )}
    </Chips>
  );
}
